import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import { CreatorDistribution, CreatorSearchItem, searchAdCreators } from '../../api/ads/ads.api';
import { CREATOR_ONLY_NOTE, MAX_NAMED_CREATORS } from '../../api/ads/ad-placements';

export interface PickedCreator {
  userId: string;
  fullName: string;
}

const OPTIONS: { value: CreatorDistribution; label: string }[] = [
  { value: 'Auto', label: 'Tự động — Cab phân phối cho creator đang bật quảng cáo' },
  { value: 'Named', label: 'Chọn đích danh creator' },
  { value: 'Marketplace', label: 'Đăng lên sàn — creator tự chọn hiện quảng cáo của bạn' },
];

interface Props {
  distribution: CreatorDistribution;
  creators: PickedCreator[];
  onChange: (distribution: CreatorDistribution, creators: PickedCreator[]) => void;
}

const CreatorDistributionSection: React.FC<Props> = ({ distribution, creators, onChange }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<CreatorSearchItem[]>([]);

  // Tìm creator có debounce 300 ms, tối thiểu 2 ký tự.
  useEffect(() => {
    if (distribution !== 'Named' || q.trim().length < 2) {
      setResults([]);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      searchAdCreators(q.trim())
        .then((r) => alive && setResults(r || []))
        .catch((e: any) => toast.error(e?.response?.data?.message || 'Không tìm được creator.'));
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, distribution]);

  const add = (c: CreatorSearchItem) => {
    if (creators.some((x) => x.userId === c.userId)) return;
    if (creators.length >= MAX_NAMED_CREATORS) {
      toast.error('Chọn từ 1 đến 20 creator.');
      return;
    }
    onChange(distribution, [...creators, { userId: c.userId, fullName: c.fullName }]);
  };
  const remove = (id: string) =>
    onChange(distribution, creators.filter((x) => x.userId !== id));

  return (
    <div>
      <div className="text-xs ui-muted mb-1">Phân phối trên nội dung creator</div>
      <div className="space-y-1.5">
        {OPTIONS.map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm ui-text cursor-pointer">
            <input
              type="radio"
              name="creator-distribution"
              checked={distribution === o.value}
              onChange={() => onChange(o.value, o.value === 'Named' ? creators : [])}
            />
            {o.label}
          </label>
        ))}
      </div>

      {distribution === 'Named' && (
        <div className="mt-2 space-y-2">
          <input
            className="w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none ui-text"
            placeholder="Tìm creator theo tên hoặc username"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {results.length > 0 && (
            <ul className="rounded-md ui-divider border max-h-48 overflow-y-auto">
              {results.map((r) => (
                <li key={r.userId}>
                  <button
                    type="button"
                    onClick={() => add(r)}
                    className="w-full text-left px-3 py-1.5 text-sm ui-text hover:bg-black/5 dark:hover:bg-white/5"
                  >
                    {r.fullName} <span className="ui-muted">@{r.userName}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            {creators.map((c) => (
              <span
                key={c.userId}
                className="inline-flex items-center gap-1 rounded-full bg-[#0071A6] text-white text-xs px-3 py-1"
              >
                {c.fullName}
                <button type="button" aria-label={`Xoá ${c.fullName}`} onClick={() => remove(c.userId)}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          <div className="text-[11px] ui-muted">
            Đã chọn {creators.length}/{MAX_NAMED_CREATORS} creator
          </div>
        </div>
      )}

      {distribution !== 'Auto' && <p className="text-[11px] ui-muted mt-2">{CREATOR_ONLY_NOTE}</p>}
    </div>
  );
};

export default CreatorDistributionSection;
